import { useState } from 'react';

import { api } from '../lib/api';
import { Alert, Badge, Card, PageHead, formatTime, useLoader } from '../components/ui';

const REASON_KIND: Record<string, string> = {
  hard_bounce: 'danger',
  complaint: 'danger',
  soft_bounce_threshold: 'warn',
  manual: 'muted',
};

const REASON_HELP: Record<string, string> = {
  hard_bounce: 'The address does not exist. Suppressed on the first occurrence.',
  complaint: 'The recipient marked it as spam. Suppressed immediately and permanently.',
  soft_bounce_threshold: 'Three transient failures within seven days.',
  manual: 'Added by you.',
};

export function Suppressions() {
  const { data, error, loading, reload } = useLoader(() => api.suppressions.list());
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const rows = (data ?? []).filter((row) =>
    search ? row.email_address.toLowerCase().includes(search.toLowerCase()) : true,
  );

  async function add(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.suppressions.add(email, 'manual');
      setEmail('');
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add address');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="Suppressions"
        subtitle="Addresses this account will not send to. Checked before every send, on both the API and SMTP paths."
      />

      {error && <Alert kind="error">{error}</Alert>}

      <Card title="Block an address">
        {formError && <Alert kind="error">{formError}</Alert>}
        <form onSubmit={add} className="row">
          <input
            aria-label="Email address to block"
            type="email"
            required
            placeholder="someone@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            style={{ maxWidth: 320 }}
          />
          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </form>
      </Card>

      <Card
        tight
        title={`Suppressed addresses (${(data ?? []).length})`}
        actions={
          <input
            aria-label="Search suppressed addresses"
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ width: 190 }}
          />
        }
      >
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Address</th>
                <th>Reason</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.email_address}</td>
                  <td>
                    <Badge kind={REASON_KIND[row.reason] ?? 'muted'}>{row.reason}</Badge>
                    <div className="hint">{REASON_HELP[row.reason] ?? ''}</div>
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {formatTime(row.created_at)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={async () => {
                        // Deliberately blunt wording. Un-suppressing a hard bounce
                        // or complaint is the one customer action that can damage
                        // platform-wide deliverability, and it is audited.
                        const warning =
                          row.reason === 'manual'
                            ? `Remove ${row.email_address} from the list?`
                            : `${row.email_address} was suppressed by a ${row.reason.replace(/_/g, ' ')}. ` +
                              'Sending to it again risks your sending reputation. This is recorded in the audit log. Continue?';
                        if (!window.confirm(warning)) return;
                        await api.suppressions.remove(row.email_address);
                        reload();
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="empty">
            {loading ? 'Loading…' : 'Nothing suppressed. That is the good outcome.'}
          </div>
        )}
      </Card>
    </>
  );
}
